import { useEffect, useState } from "react";
import { api, fmtCurrency, fmtPct } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";

const PROJECT_TYPES = [
  ["one_time", "Einmalprojekt"], ["retainer", "Retainer"], ["campaign", "Kampagne"],
  ["setup", "Setup-Projekt"], ["performance", "Performance"], ["maintenance", "Wartung"],
  ["consulting", "Beratung"], ["other", "Sonstiges"],
];
const STATUSES = [
  ["inquiry", "Anfrage", "bg-neutral-100 text-neutral-700"],
  ["offer_sent", "Angebot erstellt", "bg-blue-100 text-blue-700"],
  ["ordered", "Beauftragt", "bg-violet-100 text-violet-700"],
  ["in_progress", "In Umsetzung", "bg-amber-100 text-amber-700"],
  ["waiting_customer", "Wartet auf Kunde", "bg-yellow-100 text-yellow-700"],
  ["billing_open", "Abrechnung offen", "bg-orange-100 text-orange-700"],
  ["completed", "Abgeschlossen", "bg-emerald-100 text-emerald-700"],
  ["paused", "Pausiert", "bg-neutral-200 text-neutral-700"],
  ["cancelled", "Storniert", "bg-red-100 text-red-700"],
];

const empty = {
  customer_id: "", name: "", project_type: "one_time", status: "in_progress",
  progress_pct: 0, start_date: "", end_date: "", net_value: 0, vat_rate: 19,
  planned_external_costs: 0, actual_external_costs: 0, planned_media_budget: 0, actual_media_budget: 0,
  payment_model_id: "", notes: "",
};

export default function ProjectsPage() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [paymentModels, setPaymentModels] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [p, c, pm] = await Promise.all([api.get("/projects"), api.get("/customers"), api.get("/payment-models")]);
    setItems(p.data); setCustomers(c.data); setPaymentModels(pm.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      ...form,
      progress_pct: Number(form.progress_pct), net_value: Number(form.net_value),
      vat_rate: Number(form.vat_rate),
      planned_external_costs: Number(form.planned_external_costs),
      actual_external_costs: Number(form.actual_external_costs),
      planned_media_budget: Number(form.planned_media_budget),
      actual_media_budget: Number(form.actual_media_budget),
      payment_model_id: form.payment_model_id || null,
      start_date: form.start_date || null, end_date: form.end_date || null,
    };
    try {
      if (editing) await api.patch(`/projects/${editing.id}`, payload);
      else await api.post("/projects", payload);
      toast.success(editing ? "Projekt aktualisiert" : "Projekt angelegt");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const del = async (id) => {
    if (!window.confirm("Projekt wirklich löschen?")) return;
    await api.delete(`/projects/${id}`); toast.success("Gelöscht"); load();
  };

  const generateInvoices = async (id, name) => {
    if (!window.confirm(`Aus dem Zahlungsmodell von "${name}" jetzt Rechnungs-Entwürfe erzeugen?`)) return;
    try {
      const { data } = await api.post(`/projects/${id}/generate-invoices`);
      toast.success(`${data.created.length} Rechnungs-Entwürfe erstellt`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const totalMargin = items.reduce((s, p) => s + (p.planned_margin || 0), 0);
  const totalOutstanding = items.reduce((s, p) => s + (p.outstanding_gross || 0), 0);

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Projekte</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Kundenprojekte</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild><Button data-testid="add-project-btn"><Plus className="h-4 w-4 mr-2" />Neues Projekt</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? "Projekt bearbeiten" : "Neues Projekt"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="col-span-2"><Label>Projektname</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name" /></div>
              <div className="col-span-2">
                <Label>Kunde</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="project-customer"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                  <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Projektart</Label>
                <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PROJECT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Startdatum</Label><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>Enddatum</Label><Input type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              <div><Label>Fortschritt %</Label><Input type="number" value={form.progress_pct} onChange={(e) => setForm({ ...form, progress_pct: e.target.value })} /></div>
              <div><Label>Auftragswert netto (€)</Label><Input type="number" step="0.01" value={form.net_value} onChange={(e) => setForm({ ...form, net_value: e.target.value })} data-testid="project-net" /></div>
              <div><Label>USt-Satz %</Label><Input type="number" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} /></div>
              <div><Label>Geplante externe Kosten (€)</Label><Input type="number" step="0.01" value={form.planned_external_costs} onChange={(e) => setForm({ ...form, planned_external_costs: e.target.value })} /></div>
              <div><Label>Tatsächl. externe Kosten (€)</Label><Input type="number" step="0.01" value={form.actual_external_costs} onChange={(e) => setForm({ ...form, actual_external_costs: e.target.value })} /></div>
              <div><Label>Geplantes Media Budget (€)</Label><Input type="number" step="0.01" value={form.planned_media_budget} onChange={(e) => setForm({ ...form, planned_media_budget: e.target.value })} /></div>
              <div><Label>Tatsächl. Media Budget (€)</Label><Input type="number" step="0.01" value={form.actual_media_budget} onChange={(e) => setForm({ ...form, actual_media_budget: e.target.value })} /></div>
              <div className="col-span-2">
                <Label>Zahlungsmodell (optional — für Auto-Rechnungen)</Label>
                <Select value={form.payment_model_id || "none"} onValueChange={(v) => setForm({ ...form, payment_model_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="project-payment-model"><SelectValue placeholder="Kein Zahlungsmodell" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Keines —</SelectItem>
                    {paymentModels.map(pm => <SelectItem key={pm.id} value={pm.id}>{pm.name} — {pm.description}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Notizen</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="project-save">{editing ? "Speichern" : "Anlegen"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Aktive Projekte" value={items.filter(p => !["completed","cancelled"].includes(p.status)).length} />
        <Stat label="Auftragsvolumen netto" value={fmtCurrency(items.reduce((s,p)=>s+(p.net_value||0),0))} />
        <Stat label="Geplante Marge" value={fmtCurrency(totalMargin)} />
        <Stat label="Offene Forderungen" value={fmtCurrency(totalOutstanding)} />
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white">
        <Table data-testid="projects-table">
          <TableHeader>
            <TableRow>
              <TableHead>Projekt</TableHead><TableHead>Kunde</TableHead><TableHead>Status</TableHead>
              <TableHead>Fortschritt</TableHead><TableHead className="text-right">Netto</TableHead>
              <TableHead className="text-right">Marge</TableHead><TableHead className="text-right">Offen</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => {
              const st = STATUSES.find(s => s[0] === p.status);
              const negMargin = (p.planned_margin || 0) < 0;
              return (
                <TableRow key={p.id} className="table-row-hover" data-testid={`project-row-${p.id}`}>
                  <TableCell className="font-medium">
                    {p.name}
                    <div className="text-xs text-neutral-500">{PROJECT_TYPES.find(t => t[0] === p.project_type)?.[1]}</div>
                  </TableCell>
                  <TableCell className="text-sm">{p.customer?.company_name || "—"}</TableCell>
                  <TableCell><Badge className={st?.[2]} variant="secondary">{st?.[1] || p.status}</Badge></TableCell>
                  <TableCell>
                    <div className="w-32 h-1.5 rounded-full bg-neutral-200 overflow-hidden">
                      <div className="h-full" style={{ width: `${p.progress_pct || 0}%`, backgroundColor: "var(--tenant-primary)" }} />
                    </div>
                    <div className="text-[11px] text-neutral-500 mt-1">{fmtPct(p.progress_pct)}</div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmtCurrency(p.net_value)}</TableCell>
                  <TableCell className={`text-right tabular-nums ${negMargin ? "text-red-600 font-medium" : ""}`}>{fmtCurrency(p.planned_margin)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtCurrency(p.outstanding_gross)}</TableCell>
                  <TableCell className="text-right">
                    {p.payment_model_id && !p.auto_generated_invoices && (
                      <Button variant="ghost" size="icon" onClick={() => generateInvoices(p.id, p.name)} title="Rechnungs-Entwürfe aus Zahlungsmodell erstellen" data-testid={`project-generate-${p.id}`}><Zap className="h-4 w-4 text-amber-500" /></Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => { setEditing(p); setForm({ ...empty, ...p, start_date: p.start_date || "", end_date: p.end_date || "" }); setOpen(true); }} data-testid={`project-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(p.id)} data-testid={`project-delete-${p.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-neutral-500 py-12">Noch keine Projekte</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">{label}</div>
      <div className="font-display text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
